"use client";

import { ChevronDown, ChevronRight, NotebookPen } from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "@/shared/lib/utils/cn";
import { ledgerPreviewLine, parseLedgerForDisplay } from "./parse-ledger-display";

/**
 * Expandable ledger row — same footprint as InsightsToolStack.
 * Renders a display-only pretty view of agent KEY: value scratch notes;
 * raw transcript content is unchanged for the model.
 */
export function InsightsLedgerCard({
  content,
  live = false,
  defaultExpanded = true,
}: {
  content: string;
  live?: boolean;
  defaultExpanded?: boolean;
}) {
  const [open, setOpen] = useState(defaultExpanded);
  const preview = useMemo(() => ledgerPreviewLine(content), [content]);
  const sections = useMemo(() => parseLedgerForDisplay(content), [content]);
  const expanded = live || open;

  return (
    <div
      className={cn(
        "w-full min-w-0 rounded-tl-md rounded-tr-2xl rounded-b-2xl border border-border/70 bg-card text-[12px] shadow-sm",
        live && "border-primary/35",
      )}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 px-4 py-3 text-left text-muted-foreground"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={expanded}
      >
        {live ? (
          <span
            className="size-3.5 shrink-0 animate-spin rounded-full border-2 border-primary/25 border-t-primary"
            aria-hidden
          />
        ) : (
          <NotebookPen className="size-3.5 shrink-0" />
        )}
        <span className="shrink-0 font-medium text-foreground/80">Working notes</span>
        {!expanded ? (
          <span className="min-w-0 flex-1 truncate font-normal text-muted-foreground">
            {preview}
          </span>
        ) : (
          <span className="min-w-0 flex-1" />
        )}
        {expanded ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
      </button>
      {expanded ? (
        <div className="divide-y divide-border/40 border-t border-border/50 px-4">
          {sections.length === 0 ? (
            <p className="py-3 text-[12px] leading-5 break-words whitespace-pre-wrap text-muted-foreground [overflow-wrap:anywhere]">
              {content.trim() || "No notes yet."}
            </p>
          ) : (
            sections.map((section, index) => (
              <section key={`${index}-${section.key}`} className="min-w-0 py-3">
                <h4 className="text-[10px] font-semibold tracking-[1.15px] text-muted-foreground uppercase">
                  {section.label}
                </h4>
                {section.items?.length ? (
                  <ul className="mt-2 space-y-1.5 text-[12px] leading-5 text-foreground/85">
                    {section.items.map((item, itemIndex) => (
                      <li
                        key={`${itemIndex}-${item.slice(0, 48)}`}
                        className="flex items-start gap-2 break-words [overflow-wrap:anywhere]"
                      >
                        <span className="mt-1.5 size-1 shrink-0 rounded-full bg-foreground/35" />
                        <span className="min-w-0 flex-1">{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="mt-2 text-[12px] leading-5 break-words whitespace-pre-wrap text-foreground/85 [overflow-wrap:anywhere]">
                    {section.value || "—"}
                  </p>
                )}
              </section>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
